<template>
  <!--
    Banner treatment (mini + newLayout): the prize headline + days-left
    countdown render on the LIGHT inner panel of the new FANS LEADERBOARD band, so
    the label text uses the dark V2 `text-text` / `text-text-secondary` tokens
    instead of the white-on-brand tokens used by the legacy mini block. The prize
    title keeps the brand gradient. When no prize is set the block is hidden (the
    leaderboard section renders its own pre-prize fallback line in its place).
  -->
  <div
    v-if="mini && newLayout"
    v-show="prizePresent"
    class="flex min-w-0 flex-1 basis-0 flex-col justify-center items-start gap-0.5 self-stretch"
    data-testid="leaderboard-prize-banner"
  >
    <div
      class="min-w-0 text-text text-base lg:text-lg font-semibold font-sans leading-tight capitalize"
      data-testid="leaderboard-prize-title"
    >
      {{ prizeTitle }}
    </div>
    <div
      class="text-text-secondary text-sm lg:text-base font-medium font-sans leading-tight capitalize"
      data-testid="leaderboard-prize-countdown"
    >
      {{
        $t("channel.monthlyPrize.timeRemaining.timeLeft", {
          value: timeRemaining.value,
          unit: $t(
            `channel.monthlyPrize.timeRemaining.units.${timeRemaining.unit}${timeRemaining.value === 1 ? "" : "s"}`,
          ),
        })
      }}
    </div>
  </div>
  <div
    v-else-if="mini"
    v-show="leaderboardPrize?.getLeaderboardPrize && !loading"
  >
    <div
      class="grow shrink basis-0 self-stretch flex-col justify-center items-start gap-1 inline-flex"
    >
      <div class="self-stretch flex-col justify-start items-start gap-0.5 flex">
        <div class="text-white text-lg font-normal font-serif">
          {{
            $t("channel.monthlyPrize.title.leaderboard", {
              name: communityOrChannelName,
              period: leaderboardLength,
            })
          }}
        </div>
        <div
          class="self-stretch text-lg lg:text-xl leading-tight lg:leading-7 font-semibold font-sans text-transparent bg-clip-text bg-gradient-to-r from-text-brand via-text-accent to-text-accent-secondary capitalize"
        >
          {{ prizeTitle }}
        </div>
      </div>
      <div
        class="text-white/80 text-base font-medium font-sans leading-tight capitalize"
      >
        {{
          $t("channel.monthlyPrize.timeRemaining.timeLeft", {
            value: timeRemaining.value,
            unit: $t(
              `channel.monthlyPrize.timeRemaining.units.${timeRemaining.unit}${timeRemaining.value === 1 ? "" : "s"}`,
            ),
          })
        }}
      </div>
    </div>
  </div>
  <div
    v-else
    v-show="leaderboardPrize?.getLeaderboardPrize && !loading"
    class="flex flex-col justify-center items-center gap-5"
  >
    <p
      class="bg-brand-bgdivider-20 rounded-md text-basics-primary font-semibold px-4 py-2 text-sm"
    >
      {{
        $t(
          `channel.monthlyPrize.title.${leaderboardLength?.toLowerCase() || "monthly"}`,
        )
      }}
    </p>
    <h2
      class="text-brand-gradient text-center font-semibold capitalize tracking-tighter whitespace-pre-line !leading-tight"
      :class="{
        'text-4xl large-desktop:text-6xl': size === 'lg',
        'text-2xl large-desktop:text-3xl': size === 'sm',
      }"
    >
      {{ prizeTitle }}
    </h2>

    <p
      class="capitalize text-basics-secondary-80 max-w-52 text-center text-base large-desktop:text-lg"
    >
      {{
        $t("channel.monthlyPrize.timeRemaining.endOfMonth", {
          value: timeRemaining.value,
          unit: $t(
            `channel.monthlyPrize.timeRemaining.units.${timeRemaining.unit}${timeRemaining.value === 1 ? "" : "s"}`,
          ),
        })
      }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { useChannelStore } from "@/stores/channel.store";
import { useQuery } from "@vue/apollo-composable";
import { graphql as gql } from "@/generated";
import { computed, watch } from "vue";
import { useObjectTranslation } from "@/stores/translation.store";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
} from "@/utils/date";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import { endOfUTCMonth, endOfUTCWeek } from "@/utils/date";

const props = defineProps<{
  size: "sm" | "lg";
  mini?: boolean;
  leaderboardLength?: LeaderboardLength;
  /**
   * When true, the mini block renders the FANS LEADERBOARD banner
   * treatment (dark text on the light inner panel) instead of the legacy
   * white-on-brand mini styling. Only affects the `mini` branch; the full
   * (non-mini) prize hero is unchanged.
   */
  newLayout?: boolean;
}>();

/**
 * Surfaces whether a leaderboard prize is currently set so a parent
 * (the FANS LEADERBOARD section) can render its own pre-prize fallback in place
 * of the hidden prize block. Mirrors the mini block's `v-show` condition.
 */
const emit = defineEmits<{
  (e: "update:prizePresent", present: boolean): void;
}>();

const ChannelLeaderboardPrizeDocument = gql(/* GraphQL */ `
  query GetLeaderboardPrize(
    $channel_id: String!
    $leaderboard_length: LeaderboardLength
    $community_id: String
  ) {
    getLeaderboardPrize(
      channel_id: $channel_id
      leaderboard_length: $leaderboard_length
      community_id: $community_id
    ) {
      id
      created_at
      updated_at
      start_date
      title
      image
      enabled
      channel_id
      type

      leaderboard_prize_translations {
        title
        language
      }
    }
  }
`);

const channelStore = useChannelStore();
const themeStore = useThemeStore();

const communityOrChannelName = computed(() => {
  if (themeStore.currentTheme?.kind === AppThemeKind.community) {
    return (
      channelStore.channelCommunities?.find(
        (cc) => cc.id === themeStore.currentTheme?.id,
      )?.name ?? undefined
    );
  }
  return channelStore.channel?.description;
});

const {
  result: leaderboardPrize,
  refetch: refetchLeaderboardPrize,
  loading,
} = useQuery(
  ChannelLeaderboardPrizeDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
    start_date: new Date(),
    leaderboard_length: props.leaderboardLength,
    community_id: channelStore.selectedCommunity,
  })),
);

watch(
  () => channelStore.lastLeaderboardUpdate,
  (newDate, oldDate) => {
    if ((newDate && !oldDate) || (newDate && oldDate && newDate > oldDate)) {
      refetchLeaderboardPrize();
    }
  },
);

const prizePresent = computed(
  () => !!leaderboardPrize.value?.getLeaderboardPrize && !loading.value,
);

watch(prizePresent, (present) => emit("update:prizePresent", present), {
  immediate: true,
});

const { text: prizeTitle } = useObjectTranslation(
  computed(() => {
    const prize = leaderboardPrize.value?.getLeaderboardPrize;

    if (!prize) return null;
    return {
      authorUserId: undefined,
      originalText: prize.title,
      objectAccessors: { objectId: prize.id, objectProperty: "title" },
      translatedText: prize.leaderboard_prize_translations?.[0]?.title,
    };
  }),
);

interface TimeRemaining {
  value: number;
  unit: "day" | "hour" | "minute" | "second";
}

const timeRemaining = computed<TimeRemaining>(() => {
  const now = new Date();
  const utcNow = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds(),
    ),
  );

  const targetDate: Date =
    props.leaderboardLength === LeaderboardLength.Weekly
      ? endOfUTCWeek(utcNow, 1)
      : endOfUTCMonth(utcNow);

  // Calculate differences
  const days = differenceInDays(targetDate, utcNow);
  const hours = differenceInHours(targetDate, utcNow);
  const minutes = differenceInMinutes(targetDate, utcNow);
  const seconds = differenceInSeconds(targetDate, utcNow);

  // Return the appropriate time unit
  if (days > 1) return { value: days, unit: "day" };
  if (hours >= 1) return { value: hours, unit: "hour" };
  if (minutes >= 1) return { value: minutes, unit: "minute" };
  return { value: seconds, unit: "second" };
});
</script>
